import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Cuestionario,
  EntradaCrearCuestionario,
  EntradaCrearEscala,
  EntradaEditarCuestionario,
  EntradaGuardarPregunta,
  Escala,
  ResumenVersion,
  SetImagenes,
  Version,
} from '@copathy/shared';
import { api, apiSubir, apiSubirPost } from './api';

/**
 * M5 — cuestionarios, del lado de la pantalla.
 *
 * ⚠️ **Toda mutación devuelve la VERSIÓN COMPLETA**, y acá se escribe directo en
 * la caché con `setQueryData` en vez de invalidar. No es una optimización: armar
 * un cuestionario es agregar, mover y borrar preguntas una tras otra, y un
 * invalidate por operación deja la lista parpadeando en cada clic. El servidor ya
 * mandó el estado nuevo entero — usarlo es más correcto que volver a pedirlo.
 */

const clave = {
  lista: ['cuestionarios'] as const,
  uno: (id: number) => ['cuestionarios', id] as const,
  version: (id: number, versionId: number | null) =>
    ['cuestionarios', id, 'version', versionId] as const,
  versiones: (id: number) => ['cuestionarios', id, 'versiones'] as const,
  escalas: ['escalas'] as const,
  sets: ['cuestionarios', 'sets'] as const,
};

export function useCuestionarios(filtro: { texto?: string; activo?: 'si' | 'no' | 'todas' } = {}) {
  const parametros = new URLSearchParams({ activo: filtro.activo ?? 'si' });
  if (filtro.texto) parametros.set('texto', filtro.texto);

  return useQuery({
    queryKey: [...clave.lista, filtro.texto ?? '', filtro.activo ?? 'si'],
    queryFn: () => api.get<Cuestionario[]>(`/api/cuestionarios?${parametros.toString()}`),
  });
}

export function useCuestionario(id: number) {
  return useQuery({
    queryKey: clave.uno(id),
    queryFn: () => api.get<Cuestionario>(`/api/cuestionarios/${id}`),
    enabled: id > 0,
  });
}

/**
 * Una versión concreta, por id.
 *
 * ⚠️ **Es por id y no «la vigente» a propósito.** La primera versión de esta
 * pantalla pedía siempre `/vigente` —la publicada si la había—, y el resultado
 * era que al crear la v2 la pantalla se quedaba mostrando la v1: el borrador se
 * creaba de verdad, pero no se veía por ningún lado. Quién manda es la versión
 * que la pantalla eligió mirar, y eso no lo puede decidir el servidor.
 */
export function useVersion(cuestionarioId: number, versionId: number | null) {
  return useQuery({
    queryKey: clave.version(cuestionarioId, versionId),
    queryFn: () =>
      api.get<Version>(`/api/cuestionarios/${cuestionarioId}/versiones/${versionId ?? 0}`),
    enabled: cuestionarioId > 0 && versionId !== null,
  });
}

export function useVersiones(id: number) {
  return useQuery({
    queryKey: clave.versiones(id),
    queryFn: () => api.get<ResumenVersion[]>(`/api/cuestionarios/${id}/versiones`),
    enabled: id > 0,
  });
}

/**
 * Los sets de imágenes para escalas (CC-04).
 *
 * ⚠️ Cada mutación invalida **también** la caché de escalas y cuestionarios:
 * agregar una imagen a un set cambia qué figura le toca a cada punto de cada
 * escala que lo use, y esas escalas viajan dentro de las versiones. Invalidar
 * solo los sets dejaría el teléfono dibujando las figuras viejas.
 */
export function useSets() {
  const qc = useQueryClient();

  const lista = useQuery({
    queryKey: clave.sets,
    queryFn: () => api.get<SetImagenes[]>('/api/cuestionarios/sets'),
  });

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: clave.sets });
    void qc.invalidateQueries({ queryKey: clave.escalas });
    void qc.invalidateQueries({ queryKey: ['cuestionarios'] });
  };

  const crear = useMutation({
    mutationFn: (nombre: string) =>
      api.post<SetImagenes>('/api/cuestionarios/sets', { nombre }),
    onSuccess: refrescar,
  });

  const subir = useMutation({
    mutationFn: (v: { setId: number; archivo: File }) =>
      apiSubirPost<SetImagenes>(
        `/api/cuestionarios/sets/${v.setId}/imagenes`,
        'imagen',
        v.archivo,
      ),
    onSuccess: refrescar,
  });

  const quitar = useMutation({
    mutationFn: (v: { setId: number; itemId: number }) =>
      api.delete<SetImagenes>(`/api/cuestionarios/sets/${v.setId}/imagenes/${v.itemId}`),
    onSuccess: refrescar,
  });

  const desactivar = useMutation({
    mutationFn: (setId: number) => api.delete<SetImagenes>(`/api/cuestionarios/sets/${setId}`),
    onSuccess: refrescar,
  });

  return { lista, crear, subir, quitar, desactivar };
}

export function useEscalas() {
  return useQuery({
    queryKey: clave.escalas,
    queryFn: () => api.get<Escala[]>('/api/cuestionarios/escalas'),
    // Cambian poquísimo, y una escala publicada ya no cambia nunca más.
    staleTime: 5 * 60_000,
  });
}

/**
 * Crear y editar escalas.
 *
 * ⚠️ `useCrearEscala` **existía y nadie la llamaba** (D-134): el endpoint estaba
 * probado pero sin pantalla, así que las dos escalas del sistema entraron por
 * migración. Ahora las usa `ModalEscala`.
 */
export function useEditarEscala() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { escalaId: number; datos: EntradaCrearEscala }) =>
      api.put<Escala>(`/api/cuestionarios/escalas/${v.escalaId}`, v.datos),
    onSuccess: () => {
      // Se invalida en vez de escribir en caché: cambiar los puntos de una escala
      // cambia cómo se dibuja CADA pregunta que la usa, y esas viven en la caché
      // de las versiones, no en la de escalas.
      void qc.invalidateQueries({ queryKey: clave.escalas });
      void qc.invalidateQueries({ queryKey: ['cuestionarios'] });
    },
  });
}

/**
 * Desactivar o reactivar una escala. **Nada se borra: se desactiva.**
 *
 * Y esto **sí se permite con usos publicados**, al revés que editar los puntos:
 * desactivar no cambia lo que dice ningún punto —los cuestionarios ya publicados
 * siguen mostrando exactamente lo mismo—, solo la saca de la lista de las que se
 * pueden elegir para algo nuevo.
 */
export function useActivarEscala() {
  const qc = useQueryClient();
  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: clave.escalas });
    void qc.invalidateQueries({ queryKey: ['cuestionarios'] });
  };

  const desactivar = useMutation({
    mutationFn: (escalaId: number) => api.delete<Escala>(`/api/cuestionarios/escalas/${escalaId}`),
    onSuccess: refrescar,
  });

  const reactivar = useMutation({
    mutationFn: (escalaId: number) =>
      api.post<Escala>(`/api/cuestionarios/escalas/${escalaId}/reactivar`),
    onSuccess: refrescar,
  });

  return { desactivar, reactivar };
}

export function useCrearEscala() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (datos: EntradaCrearEscala) =>
      api.post<Escala>('/api/cuestionarios/escalas', datos),
    onSuccess: () => void qc.invalidateQueries({ queryKey: clave.escalas }),
  });
}

export function useCrearCuestionario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (datos: EntradaCrearCuestionario) =>
      api.post<Cuestionario>('/api/cuestionarios', datos),
    onSuccess: () => void qc.invalidateQueries({ queryKey: clave.lista }),
  });
}

export function useEditarCuestionario(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cambios: EntradaEditarCuestionario) =>
      api.patch<Cuestionario>(`/api/cuestionarios/${id}`, cambios),
    onSuccess: (c) => {
      qc.setQueryData(clave.uno(id), c);
      void qc.invalidateQueries({ queryKey: clave.lista });
    },
  });
}

/**
 * Las operaciones del borrador.
 *
 * Van juntas en un solo hook porque comparten el mismo efecto: dejan la versión
 * de trabajo en la caché y marcan el cuestionario como cambiado. Separarlas
 * obligaría a repetir ese cierre cinco veces, y basta con olvidarlo una para que
 * la pantalla muestre un orden viejo después de mover una pregunta.
 */
/**
 * La portada del cuestionario.
 *
 * ⚠️ **No va en `useBorrador`**, y no es un detalle de organización: la portada
 * vive en `cuestionarios`, no en la versión (migración 031). Cambiarla no abre un
 * borrador, no publica nada y no toca las preguntas — meterla en el hook del
 * borrador haría creer lo contrario a quien lea el código, y tarde o temprano
 * alguien le agregaría un `aplicar()` que refresca una versión que no cambió.
 */
export function usePortada(id: number) {
  const qc = useQueryClient();

  const aplicar = (c: Cuestionario) => {
    qc.setQueryData(clave.uno(id), c);
    void qc.invalidateQueries({ queryKey: clave.lista });
  };

  const subir = useMutation({
    mutationFn: (archivo: File) =>
      apiSubir<Cuestionario>(`/api/cuestionarios/${id}/portada`, 'portada', archivo),
    onSuccess: aplicar,
  });

  const quitar = useMutation({
    mutationFn: () => api.delete<Cuestionario>(`/api/cuestionarios/${id}/portada`),
    onSuccess: aplicar,
  });

  return { subir, quitar };
}

export function useBorrador(id: number) {
  const qc = useQueryClient();

  const aplicar = (v: Version) => {
    qc.setQueryData(clave.version(id, v.id), v);
    void qc.invalidateQueries({ queryKey: clave.uno(id) });
    void qc.invalidateQueries({ queryKey: clave.lista });
    void qc.invalidateQueries({ queryKey: clave.versiones(id) });
  };

  const abrir = useMutation({
    mutationFn: () => api.post<Version>(`/api/cuestionarios/${id}/borrador`),
    onSuccess: aplicar,
  });

  const descartar = useMutation({
    mutationFn: () => api.delete<void>(`/api/cuestionarios/${id}/borrador`),
    onSuccess: () => {
      // La versión desapareció: su entrada en caché se quita, no se refresca.
      // Refrescarla dispararía un GET a una versión que ya no existe y pintaría
      // un 404 en una pantalla que está bien.
      qc.removeQueries({ queryKey: ['cuestionarios', id, 'version'] });
      void qc.invalidateQueries({ queryKey: clave.uno(id) });
      void qc.invalidateQueries({ queryKey: clave.lista });
      void qc.invalidateQueries({ queryKey: clave.versiones(id) });
    },
  });

  const publicar = useMutation({
    mutationFn: () => api.post<Version>(`/api/cuestionarios/${id}/publicar`),
    onSuccess: aplicar,
  });

  const agregar = useMutation({
    mutationFn: (datos: EntradaGuardarPregunta) =>
      api.post<Version>(`/api/cuestionarios/${id}/preguntas`, datos),
    onSuccess: aplicar,
  });

  const guardar = useMutation({
    mutationFn: (v: { preguntaId: number; datos: EntradaGuardarPregunta }) =>
      api.put<Version>(`/api/cuestionarios/${id}/preguntas/${v.preguntaId}`, v.datos),
    onSuccess: aplicar,
  });

  const quitar = useMutation({
    mutationFn: (preguntaId: number) =>
      api.delete<Version>(`/api/cuestionarios/${id}/preguntas/${preguntaId}`),
    onSuccess: aplicar,
  });

  const reordenar = useMutation({
    mutationFn: (preguntas: number[]) =>
      api.put<Version>(`/api/cuestionarios/${id}/orden`, { preguntas }),
    onSuccess: aplicar,
  });

  /*
    La imagen de una sección SÍ es del borrador: cambia una fila de `preguntas`,
    así que el servidor devuelve la versión entera y se aplica como todo lo demás.
  */
  const subirImagen = useMutation({
    mutationFn: (v: { preguntaId: number; archivo: File }) =>
      apiSubir<Version>(
        `/api/cuestionarios/${id}/preguntas/${v.preguntaId}/imagen`,
        'imagen',
        v.archivo,
      ),
    onSuccess: aplicar,
  });

  const quitarImagen = useMutation({
    mutationFn: (preguntaId: number) =>
      api.delete<Version>(`/api/cuestionarios/${id}/preguntas/${preguntaId}/imagen`),
    onSuccess: aplicar,
  });

  return {
    abrir,
    descartar,
    publicar,
    agregar,
    guardar,
    quitar,
    reordenar,
    subirImagen,
    quitarImagen,
  };
}
